import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp, useFetch } from '../store/app';
import { Fade, SearchBox, FilterSelect, Tabs, BulkBar, BulkSelect, BulkAction, CheckBox } from './common';
import { Icon } from '../components/Icon';
import { Card, Button, Avatar, StatusBadge } from '../components/primitives';
import { st } from '../lib/status';
import { initials } from '../lib/avatar';
import { api } from '../lib/api';
import { can } from '../lib/rbac';
import { useSelection } from '../lib/useSelection';
import type { Client, Contact } from '../../shared/types';

const CLIENT_COLS = '30px 2fr 1.2fr 1.3fr 1.4fr 0.7fr 0.9fr 40px';
const CONTACT_COLS = '1.8fr 1.6fr 1.3fr 1.9fr 1.3fr 40px';
const CLIENT_STATUSES = ['active', 'planning', 'at-risk', 'blocked', 'done'];

type Tab = 'clients' | 'contacts';

export function Clients() {
  const { openDrawer, refresh, user } = useApp();
  const nav = useNavigate();
  const [tab, setTab] = useState<Tab>('clients');
  const [q, setQ] = useState('');
  const [industry, setIndustry] = useState('all');
  const [status, setStatus] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');

  const { data: clientsData } = useFetch<Client[]>('/clients');
  const { data: contactsData } = useFetch<(Contact & { client_name: string })[]>(tab === 'contacts' ? '/contacts' : null);
  const clients = clientsData || [];
  const contacts = contactsData || [];

  const industries = useMemo(() => Array.from(new Set(clients.map((c) => c.industry).filter(Boolean))).sort(), [clients]);
  const statuses = useMemo(() => Array.from(new Set(clients.map((c) => c.status).filter(Boolean))), [clients]);
  const clientNames = useMemo(() => Array.from(new Set(contacts.map((c) => c.client_name).filter(Boolean))).sort(), [contacts]);

  const filteredClients = clients.filter(
    (c) =>
      `${c.name} ${c.industry} ${c.primary_contact} ${c.hq_city}`.toLowerCase().includes(q.toLowerCase()) &&
      (industry === 'all' || c.industry === industry) &&
      (status === 'all' || c.status === status),
  );
  const filteredContacts = contacts.filter(
    (c) =>
      `${c.name} ${c.title} ${c.email} ${c.client_name}`.toLowerCase().includes(q.toLowerCase()) &&
      (clientFilter === 'all' || c.client_name === clientFilter),
  );

  const canBulk = !!user && (can(user.role, 'update', 'clients') || can(user.role, 'delete', 'clients'));
  const sel = useSelection(filteredClients.map((c) => c.id));

  const bulkUpdate = async (patch: Record<string, string>) => {
    await api.post('/clients/bulk', { action: 'update', ids: sel.ids, patch });
    sel.clear();
    refresh();
  };
  const bulkDelete = async () => {
    if (!confirm(`Delete ${sel.count} client${sel.count === 1 ? '' : 's'}? This can't be undone.`)) return;
    await api.post('/clients/bulk', { action: 'delete', ids: sel.ids });
    sel.clear();
    refresh();
  };

  return (
    <Fade>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <Tabs value={tab} onChange={(v) => setTab(v as Tab)} tabs={[{ label: 'Clients', value: 'clients' }, { label: 'Contacts', value: 'contacts' }]} />
        <SearchBox placeholder={tab === 'clients' ? 'Search clients…' : 'Search contacts…'} value={q} onChange={setQ} width={220} />
        {tab === 'clients' ? (
          <>
            <FilterSelect icon="domain" value={industry} onChange={setIndustry} options={[{ label: 'All industries', value: 'all' }, ...industries.map((i) => ({ label: i, value: i }))]} />
            <FilterSelect icon="filter_list" value={status} onChange={setStatus} options={[{ label: 'All statuses', value: 'all' }, ...statuses.map((s) => ({ label: st(s).label, value: s }))]} />
          </>
        ) : (
          <FilterSelect icon="apartment" value={clientFilter} onChange={setClientFilter} options={[{ label: 'All clients', value: 'all' }, ...clientNames.map((n) => ({ label: n, value: n }))]} />
        )}
        <div style={{ flex: 1 }} />
        {tab === 'clients' ? (
          <Button icon="add" onClick={() => openDrawer({ type: 'client', mode: 'create' })}>New client</Button>
        ) : (
          <Button icon="person_add" onClick={() => openDrawer({ type: 'contact', mode: 'create' })}>Add contact</Button>
        )}
      </div>

      {tab === 'clients' ? (
        <Card style={{ overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: CLIENT_COLS, gap: 14, padding: '10px 18px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em', alignItems: 'center' }}>
            <div>{canBulk ? <CheckBox checked={sel.allChecked} indeterminate={sel.someChecked} onChange={sel.toggleAll} /> : null}</div>
            <div>Name</div><div>Industry</div><div>HQ</div><div>Primary contact</div><div>Projects</div><div>Status</div><div />
          </div>
          {filteredClients.map((c) => (
            <div key={c.id} onClick={() => nav(`/clients/${c.id}`)} className="cc-hover-s2 cc-clickable" style={{ display: 'grid', gridTemplateColumns: CLIENT_COLS, gap: 14, padding: '12px 18px', borderBottom: '1px solid var(--border)', alignItems: 'center', background: sel.has(c.id) ? 'var(--accent-bg)' : undefined }}>
              <div>{canBulk ? <CheckBox checked={sel.has(c.id)} onChange={() => sel.toggle(c.id)} /> : null}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                <div className="cc-mono" style={{ width: 34, height: 34, flex: '0 0 auto', borderRadius: 8, background: 'var(--surface-2)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12.5, color: 'var(--text-2)' }}>{initials(c.name)}</div>
                <div style={{ fontWeight: 600, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{c.industry}</div>
              <div style={{ fontSize: 13, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 5 }}><Icon name="location_on" size={16} color="var(--text-3)" />{c.hq_city}</div>
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{c.primary_contact}</div>
              <div className="cc-mono" style={{ fontSize: 13, color: 'var(--text-2)' }}>{c.projects_label}</div>
              <div><StatusBadge badge={st(c.status)} /></div>
              <div style={{ textAlign: 'right' }}>
                <span onClick={(e) => { e.stopPropagation(); openDrawer({ type: 'client', mode: 'edit', id: c.id }); }} style={{ cursor: 'pointer', display: 'inline-flex' }}><Icon name="edit" size={18} color="var(--text-3)" /></span>
              </div>
            </div>
          ))}
          {!filteredClients.length && <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No clients match your filters.</div>}
        </Card>
      ) : (
        <Card style={{ overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: CONTACT_COLS, gap: 14, padding: '10px 18px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
            <div>Name</div><div>Title</div><div>Client</div><div>Email</div><div>Phone</div><div />
          </div>
          {filteredContacts.map((c) => (
            <div key={c.id} onClick={() => openDrawer({ type: 'contact', mode: 'edit', id: c.id })} className="cc-hover-s2 cc-clickable" style={{ display: 'grid', gridTemplateColumns: CONTACT_COLS, gap: 14, padding: '12px 18px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                <Avatar initials={initials(c.name)} bg="var(--surface-2)" fg="var(--text-2)" size={34} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                  <span style={{ fontWeight: 600, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                  {c.primary && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.03em', color: 'var(--accent)', background: 'var(--accent-bg)', padding: '1px 6px', borderRadius: 4, whiteSpace: 'nowrap' }}>PRIMARY</span>}
                </div>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{c.title}</div>
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{c.client_name}</div>
              <div style={{ fontSize: 13, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</div>
              <div className="cc-mono" style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{c.phone}</div>
              <div style={{ textAlign: 'right' }}><Icon name="edit" size={18} color="var(--text-3)" style={{ cursor: 'pointer' }} /></div>
            </div>
          ))}
          {!filteredContacts.length && <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No contacts match your filters.</div>}
        </Card>
      )}

      {tab === 'clients' && canBulk && (
        <BulkBar count={sel.count} onClear={sel.clear}>
          {can(user!.role, 'update', 'clients') && (
            <>
              <BulkSelect label="Set status" icon="flag" onPick={(v) => bulkUpdate({ status: v })} options={CLIENT_STATUSES.map((s) => ({ label: st(s).label, value: s }))} />
              {industries.length > 0 && <BulkSelect label="Set industry" icon="domain" onPick={(v) => bulkUpdate({ industry: v })} options={industries.map((i) => ({ label: i, value: i }))} />}
            </>
          )}
          {can(user!.role, 'delete', 'clients') && <BulkAction label="Delete" icon="delete" danger onClick={bulkDelete} />}
        </BulkBar>
      )}
    </Fade>
  );
}
